import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  BreadcrumbsBuilder,
  type SmartBreadcrumbItem,
} from './BreadcrumbsBuilder';
import { BreadcrumbsContext, type BreadcrumbLayer } from './BreadcrumbsContext';
import { useMergedBreadcrumbs } from './useBreadcrumbs';

type BreadcrumbsProviderProps = {
  children: ReactNode;
};

type BreadcrumbsOutletProps = {
  maxVisible?: number;
  keepFirst?: number;
  keepLast?: number;
  className?: string;
  listClassName?: string;
  itemClassName?: string;
  currentClassName?: string;
};

export function BreadcrumbsProvider({ children }: BreadcrumbsProviderProps) {
  const [layers, setLayers] = useState<Record<string, BreadcrumbLayer>>({});
  const sequence = useRef(0);

  const setLayer = useCallback(
    (id: string, items: SmartBreadcrumbItem[], priority: number) => {
      setLayers((previous) => {
        const existingLayer = previous[id];
        const nextLayer: BreadcrumbLayer = {
          id,
          items,
          priority,
          order: existingLayer?.order ?? sequence.current++,
        };
        return { ...previous, [id]: nextLayer };
      });
    },
    [],
  );

  const clearLayer = useCallback((id: string) => {
    setLayers((previous) => {
      if (!previous[id]) {
        return previous;
      }
      const next = { ...previous };
      delete next[id];
      return next;
    });
  }, []);

  const items = useMemo(
    () =>
      Object.values(layers)
        .sort((a, b) => b.priority - a.priority || b.order - a.order)
        .flatMap((layer) => layer.items),
    [layers],
  );

  const value = useMemo(
    () => ({
      setLayer,
      clearLayer,
      items,
    }),
    [setLayer, clearLayer, items],
  );

  return (
    <BreadcrumbsContext.Provider value={value}>
      {children}
    </BreadcrumbsContext.Provider>
  );
}

export function BreadcrumbsOutlet({
  maxVisible,
  keepFirst,
  keepLast,
  className,
  listClassName,
  itemClassName,
  currentClassName,
}: BreadcrumbsOutletProps) {
  const items = useMergedBreadcrumbs();

  return (
    <BreadcrumbsBuilder
      items={items}
      maxVisible={maxVisible}
      keepFirst={keepFirst}
      keepLast={keepLast}
      className={className}
      listClassName={listClassName}
      itemClassName={itemClassName}
      currentClassName={currentClassName}
    />
  );
}
